import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/client';
import { fetchSseToken } from '../api/sseToken';
import TickerAutocomplete from './TickerAutocomplete';
import { formatCurrency } from '../lib/formatCurrency';
import { getNotifRoute } from '../lib/notificationRoutes';

function TradeSignalModal({ notif, onClose, onExecuted }) {
  const m = notif.metadata || {};
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [done, setDone]       = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const onKeyDown = e => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = [...modalRef.current.querySelectorAll(FOCUSABLE)].filter(el => !el.disabled);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    modalRef.current?.querySelector('button')?.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const actionLabel = m.action === 'sell' ? 'Sell' : 'Buy';
  const priceDisplay = m.signalPrice ? `@ ${formatCurrency(Number(m.signalPrice))}` : '';
  const scoreDisplay = m.score != null ? `${Math.round(m.score * 100)}% confidence` : null;
  const signalTime   = notif.created_at ? new Date(notif.created_at).toLocaleString() : null;

  async function handlePlace() {
    setLoading(true);
    setError(null);
    try {
      await api.post(`/automations/execute-signal/${m.executionId}`);
      setDone(true);
      onExecuted();
      setTimeout(onClose, 1800);
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) setError('This signal was already processed.');
      else if (status === 410) setError('Signal expired — market closed after 4 PM ET.');
      else if (status === 422) setError('Missing trade parameters. Cannot place order.');
      else setError('Network error. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="signal-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="signal-modal-title">
      <div ref={modalRef} className="signal-modal">
        <div className="signal-modal-header">
          <span id="signal-modal-title" className="signal-modal-title">Confirm Trade Signal</span>
          <button className="signal-modal-close" onClick={onClose} aria-label="Dismiss">
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {done ? (
          <div className="signal-modal-success">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>
            Order placed for {m.quantity}× {m.ticker}
          </div>
        ) : (
          <>
            <div className="signal-modal-ticker">
              <span className={`signal-modal-action signal-modal-action--${m.action}`}>{actionLabel}</span>
              <span className="signal-modal-symbol">{m.ticker}</span>
            </div>
            <dl className="signal-modal-details">
              <div>
                <dt>Quantity</dt>
                <dd>{m.quantity} share{m.quantity !== 1 ? 's' : ''}</dd>
              </div>
              {priceDisplay && (
                <div>
                  <dt>Signal price</dt>
                  <dd>{priceDisplay}</dd>
                </div>
              )}
              {scoreDisplay && (
                <div>
                  <dt>Score</dt>
                  <dd>{scoreDisplay}</dd>
                </div>
              )}
              {signalTime && (
                <div>
                  <dt>Generated</dt>
                  <dd>{signalTime}</dd>
                </div>
              )}
            </dl>

            {error && <div className="signal-modal-error" role="alert">{error}</div>}

            <div className="signal-modal-actions">
              <button className="signal-modal-btn signal-modal-btn--secondary" onClick={onClose} disabled={loading}>
                Dismiss
              </button>
              <button className="signal-modal-btn signal-modal-btn--primary" onClick={handlePlace} disabled={loading}>
                {loading ? 'Placing…' : 'Place order'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// onNavigate: fires right after a real navigation (select or Enter), never on Escape/blank —
// lets the mobile expanded-search row close itself once its job is done, without this
// component needing to know anything about mobile vs. desktop.
function NavTickerSearch({ inputClassName = 'nav-search-input', containerClassName, autoFocus, onNavigate, onEscape }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  function handleSelect({ symbol }) {
    const isCrypto = symbol.includes('/');
    if (isCrypto) {
      const [cryptoBase, cryptoQuote] = symbol.split('/');
      const path = cryptoQuote === 'USD'
        ? `/ticker/crypto/${cryptoBase}`
        : `/ticker/crypto/${cryptoBase}/${cryptoQuote}`;
      navigate(path);
    } else {
      navigate(`/ticker/${symbol}`);
    }
    setQuery('');
    onNavigate?.();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && query.trim()) {
      const q = query.trim().toUpperCase();
      if (q.includes('/')) {
        const [cryptoBase, cryptoQuote] = q.split('/');
        const path = cryptoQuote === 'USD'
          ? `/ticker/crypto/${cryptoBase}`
          : `/ticker/crypto/${cryptoBase}/${cryptoQuote}`;
        navigate(path);
      } else {
        navigate(`/ticker/${q}`);
      }
      setQuery('');
      onNavigate?.();
    }
    if (e.key === 'Escape') { setQuery(''); onEscape?.(); }
  }

  return (
    <TickerAutocomplete
      value={query}
      onChange={setQuery}
      onSelect={handleSelect}
      onKeyDown={handleKeyDown}
      dropdownAlign="right"
      placeholder="Search ticker…"
      inputClassName={inputClassName}
      className={containerClassName}
      autoFocus={autoFocus}
    />
  );
}

function fmtAge(created_at) {
  // Spell out units — "9m ago" was misread as "9 months". This is when the notification
  // was surfaced, not the underlying transaction date.
  const sec = Math.floor((Date.now() - new Date(created_at)) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)} min ago`;
  if (sec < 86400) { const n = Math.floor(sec / 3600); return `${n} hr${n === 1 ? '' : 's'} ago`; }
  const d = Math.floor(sec / 86400);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export default function AppNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [open, setOpen]                   = useState(false);
  const [openMenu, setOpenMenu]           = useState(null); // 'money' | 'markets' | null
  const [signalModal, setSignalModal]     = useState(null); // notif object | null
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false); // ticker search — .nav-search-input hides below 480px with no replacement, this is it
  const bellRef                           = useRef(null);
  const mobileSearchRef                   = useRef(null);
  const genRef                            = useRef(0); // incremented each fetch; stale responses ignored
  const sseRetryCount                     = useRef(0);

  // A tapped trade-signal push arrives as ?notification=<id> (v1.0.2 §6c). The dialog opens from
  // the server's copy in this list, never from the push payload; the param is cleared either way.
  const pendingNotificationId = new URLSearchParams(location.search).get('notification');
  useEffect(() => {
    if (!pendingNotificationId || !notificationsLoaded) return;
    const signal = notifications.find(n => n.id === pendingNotificationId && n.type === 'trade_signal');
    if (signal) setSignalModal(signal);
    const rest = new URLSearchParams(location.search);
    rest.delete('notification');
    const query = rest.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash}`, { replace: true });
  }, [pendingNotificationId, notificationsLoaded, notifications, location.pathname, location.search, location.hash, navigate]);

  const fetchNotifications = useCallback(() => {
    if (document.hidden) return; // skip when tab is not visible
    const gen = ++genRef.current;
    api.get('/notifications').then(res => {
      if (gen !== genRef.current) return; // stale — a newer fetch is in flight
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
      setNotificationsLoaded(true);
    }).catch(() => {
      // background poll — silently ignore all errors (network, 401)
    });
  }, []);

  // Real-time delivery via SSE (server pushes the instant a notification is created) instead
  // of a fixed-interval poll — a 60s poll kept Neon's compute permanently active (every open
  // tab queried the DB well under its 5-min idle-suspend threshold, 24/7). Fetch on mount and
  // on tab-visibility resume; a slow (6 min, safely above that threshold) fallback poll covers
  // a silently dropped SSE connection without recreating the original problem.
  useEffect(() => {
    fetchNotifications();
    const fallbackPollId = setInterval(fetchNotifications, 6 * 60_000);
    const onVisibility = () => { if (!document.hidden) fetchNotifications(); };
    document.addEventListener('visibilitychange', onVisibility);

    const SSE_BASE = process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:5000/api/v1');
    let es = null;
    let cancelled = false;

    function connectStream() {
      if (!localStorage.getItem('token')) return;
      fetchSseToken()
        .then((token) => {
          if (cancelled || !token) return;
          es = new EventSource(`${SSE_BASE}/notifications/stream?token=${encodeURIComponent(token)}`);
          es.onmessage = () => {
            sseRetryCount.current = 0; // successful push — reset retry counter
            fetchNotifications(); // refetch the full list/unread count on any push
          };
          // One-time token is consumed on connect; if EventSource drops and auto-reconnects
          // it will hit 401. Intercept the error and re-establish with a fresh token.
          es.onerror = () => {
            if (cancelled) return;
            es.close();
            if (sseRetryCount.current >= 5) return; // give up after 5 consecutive failures — fallback poll still covers us
            sseRetryCount.current += 1;
            setTimeout(connectStream, 2000);
          };
        })
        .catch(() => { /* silent — fallback poll still covers us */ });
    }
    connectStream();

    return () => {
      cancelled = true;
      clearInterval(fallbackPollId);
      document.removeEventListener('visibilitychange', onVisibility);
      genRef.current = 0; // invalidate any in-flight request on unmount
      if (es) es.close();
    };
  }, [fetchNotifications]);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (bellRef.current && !bellRef.current.contains(e.target)) setOpen(false);
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  // Mobile ticker search — tapping outside it (or Escape) closes it, same pattern as the
  // notification dropdown above, instead of requiring an explicit close button.
  useEffect(() => {
    if (!mobileSearchOpen) return;
    function handleClick(e) {
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(e.target)) setMobileSearchOpen(false);
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setMobileSearchOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileSearchOpen]);

  function toggleOpen() {
    setOpen(prev => !prev);
    if (!open && unreadCount > 0) {
      // Optimistic clear — server update fires in background
      api.post('/notifications/read-all').then(() => {
        setUnreadCount(0);
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      }).catch(() => fetchNotifications());
    }
  }

  function markRead(id) {
    api.patch(`/notifications/${id}/read`).catch(() => fetchNotifications());
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }

  function deleteNotif(id, wasUnread) {
    api.delete(`/notifications/${id}`).catch(() => fetchNotifications());
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
  }

  function clearAllNotifs() {
    api.delete('/notifications').catch(() => fetchNotifications());
    setNotifications([]);
    setUnreadCount(0);
  }

  return (
    <>
    <header className="nav">
      <Link to="/" className="nav-brand">Agence</Link>
      <nav>
        <div className="nav-primary">
        <Link to="/insights">Insights</Link>

        <div
          className={`nav-dropdown${openMenu === 'money' ? ' nav-dropdown--open' : ''}`}
          onMouseEnter={() => setOpenMenu('money')}
          onMouseLeave={() => setOpenMenu(null)}
          onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpenMenu(null); }}
        >
          <span
            role="button"
            tabIndex={0}
            className="nav-dropdown-trigger"
            aria-haspopup="menu"
            aria-expanded={openMenu === 'money'}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenMenu(m => m === 'money' ? null : 'money'); }
              if (e.key === 'Escape') setOpenMenu(null);
            }}
          >Money</span>
          <div className="nav-dropdown-menu">
            <Link to="/budgets" onClick={() => setOpenMenu(null)}>Budgets</Link>
            <Link to="/expenses" onClick={() => setOpenMenu(null)}>Expenses</Link>
            <Link to="/goals" onClick={() => setOpenMenu(null)}>Goals</Link>
          </div>
        </div>

        <div
          className={`nav-dropdown${openMenu === 'markets' ? ' nav-dropdown--open' : ''}`}
          onMouseEnter={() => setOpenMenu('markets')}
          onMouseLeave={() => setOpenMenu(null)}
          onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpenMenu(null); }}
        >
          <span
            role="button"
            tabIndex={0}
            className="nav-dropdown-trigger"
            aria-haspopup="menu"
            aria-expanded={openMenu === 'markets'}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenMenu(m => m === 'markets' ? null : 'markets'); }
              if (e.key === 'Escape') setOpenMenu(null);
            }}
          >Markets</span>
          <div className="nav-dropdown-menu">
            <Link to="/automations" onClick={() => setOpenMenu(null)}>Automations</Link>
            <Link to="/portfolio" onClick={() => setOpenMenu(null)}>Portfolio</Link>
            <Link to="/watchlist" onClick={() => setOpenMenu(null)}>Watchlist</Link>
          </div>
        </div>

        <Link to="/settings">Account</Link>
        </div>

        <NavTickerSearch />

        {/* Mobile ticker search — .nav-search-input (above) hides entirely below 480px.
            Below that breakpoint this renders either the compact icon or, once tapped, an
            input that takes over exactly the icon's row slot via flex:1 (see
            .nav-mobile-search-expanded / .nav > nav in index.css) — it expands in place
            alongside the brand and bell rather than hiding them or adding a new row.
            Closes on outside click/Escape (see the effect above) rather than a close
            button, so it doesn't compete with the bell for the same sliver of row width. */}
        {mobileSearchOpen ? (
          <div className="nav-mobile-search-expanded" ref={mobileSearchRef}>
            <NavTickerSearch
              inputClassName="nav-mobile-search-input"
              containerClassName="nav-mobile-search-input-wrap"
              autoFocus
              onNavigate={() => setMobileSearchOpen(false)}
            />
          </div>
        ) : (
          <button
            className="nav-mobile-search-btn"
            onClick={() => setMobileSearchOpen(true)}
            aria-label="Search ticker"
            aria-expanded={false}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>
        )}

        {/* Notification bell */}
        <div className="notif-bell" ref={bellRef}>
          <button
            className="notif-bell-btn"
            onClick={toggleOpen}
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            {unreadCount > 0 && (
              <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>

          {open && (
            <div className="notif-dropdown">
              <div className="notif-header">
                <span>Notifications</span>
                {notifications.length > 0 && (
                  <button
                    className="notif-clear-all"
                    onClick={clearAllNotifs}
                  >
                    Clear all
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div className="notif-empty">No notifications yet</div>
              ) : (
                <ul className="notif-list">
                  {notifications.map(n => {
                    const route = getNotifRoute(n);
                    return (
                      <li
                        key={n.id}
                        className={`notif-item${n.is_read ? '' : ' notif-item--unread'}${(route || n.type === 'trade_signal') ? ' notif-item--linked' : ''}`}
                        style={{ position: 'relative' }}
                      >
                        <button
                          style={{ display: 'block', width: '100%', background: 'none', border: 'none', padding: 0, cursor: (route || n.type === 'trade_signal') ? 'pointer' : 'default', textAlign: 'left', paddingRight: '1.5rem' }}
                          aria-label={n.title}
                          onClick={() => {
                            if (!n.is_read) markRead(n.id);
                            if (n.type === 'trade_signal') {
                              setOpen(false);
                              setSignalModal(n);
                            } else if (route) {
                              setOpen(false);
                              navigate(route);
                            }
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="notif-title">{n.title}</div>
                            {n.body && <div className="notif-body">{n.body}</div>}
                            <div className="notif-age">
                              {fmtAge(n.created_at)}
                              {(route || n.type === 'trade_signal') && <span style={{ marginLeft: '0.4rem', color: 'var(--navy-400)', fontSize: '0.7rem' }}>{n.type === 'trade_signal' ? '→ Review' : '→ View'}</span>}
                            </div>
                          </div>
                        </button>
                        <button
                          className="icon-btn"
                          onClick={e => { e.stopPropagation(); deleteNotif(n.id, !n.is_read); }}
                          style={{ position: 'absolute', top: '50%', right: '0.25rem', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1rem', padding: '0.25rem', lineHeight: 1 }}
                          aria-label="Dismiss notification"
                        ><svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg></button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>
      </nav>
    </header>
    {signalModal && (
      <TradeSignalModal
        notif={signalModal}
        onClose={() => setSignalModal(null)}
        onExecuted={fetchNotifications}
      />
    )}
    </>
  );
}
